<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";
import { useTemplating, buildTemplateContext } from "#bb/composables/useTemplating";
import { resolveSiteFileDownloadUrl } from "#bb/lib/pathContract";

const logger = useLogger("Hero");

// 1. Expect 'id' from the parent.
const props = defineProps<{ id: string }>();
const config = useRuntimeConfig();
const { siteId } = useSite();
const features = inject<{
  resourceDefinitionPath?: string | null;
}>("render-features", {});

// 2. Access the definition data (read-only) from the content manager.
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

// Inject report provider (optional)
const report = inject<any | null>("report", null);
const templateContext = computed(() => buildTemplateContext(report));
const { applyTemplate, applyTemplateObject } = useTemplating(templateContext, { logger });

// Computed templated properties
const templated = computed(() => applyTemplateObject({
  title: definitionData.value?.title || "",
  subtitle: definitionData.value?.subtitle || "",
  image: definitionData.value?.image || ""
}));
const imageSource = computed(() => resolveSiteFileDownloadUrl(
  templated.value.image,
  config.public.apiPublicUrl || config.public.apiUrl,
  siteId.value,
  features.resourceDefinitionPath,
));

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Building block editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the hero",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

// Expose the commands and properties
defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Hero",
    icon: "lucide:heading",
  },
});
</script>

<template>
  <div class="relative">
    <div class="absolute inset-0">
      <img class="w-full h-full object-cover" :src="imageSource" alt="" />
      <div
        class="absolute inset-0 bg-primary/50 mix-blend-multiply"
        aria-hidden="true"
      />
    </div>
    <div class="relative max-w-7xl mx-auto py-24 px-4 @min-[640px]:py-32 @min-[640px]:px-6 @min-[1024px]:px-8">
      <h1
        class="text-4xl font-extrabold tracking-tight text-white @min-[640px]:text-5xl @min-[1024px]:text-6xl"
      >
        {{ templated.title }}
      </h1>
      <p class="mt-6 text-xl text-white max-w-3xl">
        {{ templated.subtitle }}
      </p>
    </div>
  </div>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.HERO,
  icon: "lucide:image",
  editor: () => import("#bb/components/editor/content/hero.vue"),
  type: "Hero",
  description:
    "A visually striking hero section featuring a background image, title, and subtitle.",
  default: {
    title: "Discover Amazing Places",
    subtitle: "Embark on your next adventure",
    image: "",
  },
  categories: ["Content"],
    order: 13021,
};
</script>
